import DevsCard from "@/components/fichas/desarrollo/CardDevs";
import FeaturedPropCard from "@/components/general/FeaturedPropCard";

// Operation-aware price pick mirrors ResultsPane's; markers and the
// InfoWindow preview should agree with whatever the card grid shows
// for the same listing.
export const pickPriceField = (item, operation, suffix = "") => {
    const wants = (op) => operation?.includes(op);
    const sale = item[`sale${suffix}`];
    const lease = item[`lease${suffix}`];
    const presale = item[`presale${suffix}`];
    if (wants("LEASE") && lease) return lease;
    if (wants("SALE") && sale) return sale;
    if (wants("PRESALE") && presale) return presale;
    return sale || lease || presale || null;
};

const formatPropertyAddress = (locationProps) => {
    if (!locationProps) return "";
    const parts = [
        locationProps.street_name,
        locationProps.exterior_number,
        locationProps.city ? `, ${locationProps.city}` : "",
        locationProps.province ? `, ${locationProps.province}` : "",
        locationProps.country ? `, ${locationProps.country}` : "",
    ].filter(Boolean);
    return parts.join(" ").replace(/\s+,/g, ",");
};

const formatDevLocation = (dev) =>
    dev.location_props?.name ||
    [
        dev.location_props?.street_name,
        dev.location_props?.exterior_number && `${dev.location_props.exterior_number}`,
        dev.location_props?.city && `, ${dev.location_props.city}`,
        dev.location_props?.province && `, ${dev.location_props.province}`,
    ]
        .filter(Boolean)
        .join(" ");

const formatDevSubtitle = (dev) => {
    const fallback =
        dev.min_price && dev.min_price !== "1" ? dev.min_price : dev.min_sale || dev.min_lease;
    const raw = dev.min_presale && dev.min_presale !== "1" ? dev.min_presale : fallback;
    if (!raw) return "";
    return `Desde $${Number(raw).toLocaleString("es-MX")} MXN`;
};

// Shared card body for a unified search entry — used by ResultsPane's grid
// AND MapPane's marker-click InfoWindow. Both surfaces render the SAME card
// for a given listing so the user sees a consistent shape whether they
// found it in the list or clicked it on the map.
//
// Favorite-related props (session, userFavorites, savePropsFavorites,
// saveDevFavorites) are forwarded as-is. They may be undefined during P4
// favorites wiring; both card components already tolerate that.
const EntryCard = ({
    entry,
    idx = 0,
    state,
    session,
    userFavorites,
    savePropsFavorites,
    saveDevFavorites,
}) => {
    if (entry.kind === "property") {
        const item = entry.data;
        return (
            <FeaturedPropCard
                id={idx}
                presale={item.presale}
                sale={item.sale}
                lease={item.lease}
                title={item.title}
                address={formatPropertyAddress(item.location_props)}
                groundSize={
                    item.structure_props?.ground_size || item.structure_props?.construction_size
                }
                bedrooms={item.structure_props?.bedrooms}
                bathrooms={item.structure_props?.bathrooms}
                parkingLots={item.structure_props?.parking_lots}
                price={pickPriceField(item, state?.operation)}
                propertyType={item.type}
                imageUrl={item.photo}
                wiggotCode={item.wiggot_code}
                session={session}
                property_id={item.property_id}
                savePropsFavorites={savePropsFavorites}
                userFavorites={userFavorites}
                currency={pickPriceField(item, state?.operation, "_currency") || "MXN"}
                isForMap
            />
        );
    }

    const dev = entry.data;
    return (
        <DevsCard
            isCentered={false}
            isForMap
            id={dev.wiggot_code}
            title={dev.name}
            propertyType={
                dev.tipo === "Departamento" || dev.tipo === "Terreno" ? `${dev.tipo}s` : dev.tipo
            }
            subTitle={formatDevSubtitle(dev)}
            imageUrl={
                dev.photo || "https://media.grupoguia.mx/assets/home-page/fichas/nodisponible.webp"
            }
            link={`/desarrollo/${dev.url_name}`}
            buttonText="Ver más"
            referred={true}
            location={formatDevLocation(dev)}
            groundSizeMin={
                dev.product_props?.min_construction_size > 0
                    ? dev.product_props?.min_construction_size
                    : dev.product_props?.min_ground_size
            }
            groundSizeMax={
                dev.product_props?.max_construction_size > 0
                    ? dev.product_props?.max_construction_size
                    : dev.product_props?.max_ground_size
            }
            bedroomsMin={dev.product_props?.min_bedrooms}
            bedroomsMax={dev.product_props?.max_bedrooms}
            industrial={false}
            online_sale={dev.online_sale_status === "ACTIVE"}
            operation_type={dev.operation_type}
            session={session}
            saveDevFavorites={saveDevFavorites}
            development_id={dev.id}
            userFavorites={userFavorites}
            isSearchCard={true}
        />
    );
};

export default EntryCard;
